import { FC, useEffect, useRef } from 'react';
import FormUser from '@pages/ComponentsPage/Step/FormUser';
import PaymentOverview from '@pages/ComponentsPage/Step/PaymentOverview';
import PaymentMethod from '@pages/ComponentsPage/Step/PaymentMethod';
import PaymentResult from '@pages/ComponentsPage/Step/PaymentResult';
import AccountBindingState from '@pages/ComponentsPage/Step/AccountBindingState';
// import SuccessResult from './SuccessResult';
import { CheckoutStateValue, DataDetailCheckout } from '@pages/types';
import { CheckoutStep, useCheckoutStore } from '@stores/useCheckoutStore';

type BusinessSettings = {
  primary_color: string;
  secondary_color: string;
  sidebar_text_color: string;
  sidebar_text_secondary_color: string;
  button_color: string;
  button_text_color: string;
  default: string;
  theme_name: string;
};

interface CheckoutContentProps {
  data: DataDetailCheckout;
  onRefreshData: () => Promise<unknown>;
  settings: BusinessSettings;
}

const CheckoutContent: FC<CheckoutContentProps> = ({
  data,
  onRefreshData,
  settings,
}) => {
  const { currentStep, goToPayment, setStep } = useCheckoutStore();
  const hasInitializedStepFromCheckoutState = useRef(false);

  const mapCheckoutStateToStep = (state?: CheckoutStateValue): CheckoutStep => {
    switch (state) {
      case 'CUSTOMER_REQUIRED':
        return 'form';
      case 'PAYMENT_METHOD_REQUIRED':
        return 'method';
      case 'ACCOUNT_BINDING_REQUIRED':
      case 'ACCOUNT_BINDING_PROGRESS':
      case 'ACCOUNT_BINDING_FAILED':
        return 'account-binding';
      case 'ACCOUNT_BINDING_SUCCESS':
        return 'method';
      case 'PAYMENT_IN_PROGRESS':
      case 'PAYMENT_REDIRECT_REQUIRED':
        return 'payment';
      default:
        return 'form';
    }
  };

  useEffect(() => {
    if (hasInitializedStepFromCheckoutState.current) return;

    const initialStep = mapCheckoutStateToStep(data?.checkout_state);
    if (initialStep !== currentStep) {
      setStep(initialStep);
    }

    hasInitializedStepFromCheckoutState.current = true;
  }, [currentStep, setStep, data?.checkout_state]);

  if (currentStep === 'form') {
    return (
      <FormUser data={data} settings={settings} onRefresh={onRefreshData} />
    );
  }

  if (currentStep === 'overview') {
    return <PaymentOverview data={data} settings={settings} />;
  }

  if (currentStep === 'method') {
    return (
      <PaymentMethod
        data={data}
        settings={settings}
        onPaymentSelected={goToPayment}
        onRefreshData={onRefreshData}
      />
    );
  }

  if (currentStep === 'account-binding') {
    return (
      <AccountBindingState
        data={data}
        settings={settings}
        onRefreshData={onRefreshData}
      />
    );
  }

  if (currentStep === 'payment') {
    return (
      <PaymentResult
        data={data}
        settings={settings}
        onRefreshData={onRefreshData}
      />
    );
  }

  return <FormUser data={data} settings={settings} onRefresh={onRefreshData} />;
};

export default CheckoutContent;
